import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Socket } from 'socket.io-client';
import { useDraggablePanel } from '../utils/panelDragUtils';
import { playRandomCoinsGainSound, playRandomLootCollectSound, playSound, SFX_KEYS } from '../game/systems/SoundManager';

interface ItemDefinition {
  id: string;
  name: string;
  description: string;
  type: 'weapon' | 'armor' | 'accessory' | 'consumable' | 'material';
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  equipSlot?: string;
  stats?: Record<string, number>;
  buyPrice: number;
  sellPrice: number;
  icon: string;
}

interface LootSlotView {
  slotIndex: number;
  identified: boolean;
  itemId?: string;
  quantity?: number;
  item?: ItemDefinition;
}

interface LootContainerView {
  id: string;
  x: number;
  y: number;
  tileX: number;
  tileY: number;
  pieces: number;
  xp: number;
  publicAt: number;
  expiresAt: number;
  slots: LootSlotView[];
}

interface IdentifyingState {
  slotIndex: number;
  startAt: number;
  durationMs: number;
}

interface LootContainerPanelProps {
  socket: Socket | null;
  isOpen: boolean;
  containerId: string | null;
  onClose: () => void;
}

// Rarity colors matching inventory
const RARITY_COLORS: Record<string, string> = {
  common: '#9e9e9e',
  uncommon: '#4caf50',
  rare: '#2196f3',
  epic: '#9c27b0',
  legendary: '#ff9800',
};

const isContrabandItemId = (itemId: string | undefined): boolean => {
  if (!itemId) return false;
  return itemId.includes('contraband');
};

const getIconSrc = (icon: string | undefined, itemId: string | undefined): string => {
  const publicUrl: string = process.env.PUBLIC_URL || '';
  if (isContrabandItemId(itemId)) {
    return `${publicUrl}/assets/portraits/generic_contraband.png`;
  }
  if (icon && icon.startsWith('/assets')) {
    return `${publicUrl}${icon}`;
  }
  if (icon && (icon.startsWith('http://') || icon.startsWith('https://'))) {
    return icon;
  }
  // Use blueprint fallback for blueprint items
  if (itemId && itemId.startsWith('blueprint_')) {
    return `${publicUrl}/assets/items/blueprints/blueprint_fallback.png`;
  }
  return `${publicUrl}/assets/unknown.png`;
};

export const LootContainerPanel: React.FC<LootContainerPanelProps> = ({
  socket,
  isOpen,
  containerId,
  onClose,
}) => {
  const [view, setView] = useState<LootContainerView | null>(null);
  const [error, setError] = useState<string>('');
  const [identifying, setIdentifying] = useState<Record<number, IdentifyingState>>({});
  const [tooltip, setTooltip] = useState<{ visible: boolean; x: number; y: number; slot: LootSlotView | null }>({
    visible: false, x: 0, y: 0, slot: null
  });
  const { handleHeaderMouseDown, style: dragStyle } = useDraggablePanel(200, 150);

  const slotsByIndex: Record<number, LootSlotView> = useMemo(() => {
    const map: Record<number, LootSlotView> = {};
    if (!view || !view.slots || !Array.isArray(view.slots)) return map;
    for (const s of view.slots) {
      map[s.slotIndex] = s;
    }
    return map;
  }, [view]);

  const requestOpen = useCallback((): void => {
    if (!socket || !containerId) return;
    setError('');
    
    // Check if this is a sanctum container (pre-placed or NPC-dropped)
    const isSanctumContainer = containerId.startsWith('container_') || containerId.startsWith('loot_');
    
    if (isSanctumContainer) {
      // For sanctum containers, get the scene to access sanctum data
      const game = (window as any).__PHASER_GAME__;
      if (game) {
        const oceanScene = game.scene.getScene('OceanScene') as any;
        if (oceanScene && oceanScene.currentSanctumInstanceId) {
          const localShip = oceanScene.localShip;
          socket.emit('sanctum:loot_container', {
            instanceId: oceanScene.currentSanctumInstanceId,
            containerId: containerId,
            playerX: localShip?.x || 0,
            playerY: localShip?.y || 0,
          });
          return;
        }
      }
    }
    
    // Fall back to ocean loot container system
    socket.emit('lootcontainer:open', { id: containerId });
  }, [socket, containerId]);

  useEffect(() => {
    if (!socket) return;

    const onView = (data: LootContainerView): void => {
      // Ensure slots is always an array
      const normalizedData: LootContainerView = {
        ...data,
        slots: Array.isArray(data.slots) ? data.slots : [],
      };
      setView(normalizedData);
      setError('');
    };

    const onError = (data: { message: string }): void => {
      setError(data.message);
    };

    const onIdentifyStarted = (data: { id: string; slotIndex: number; durationMs: number }): void => {
      if (!containerId || data.id !== containerId) return;
      // Play research/identify start sound
      playSound(SFX_KEYS.RESEARCH_START);
      setIdentifying((prev) => ({
        ...prev,
        [data.slotIndex]: {
          slotIndex: data.slotIndex,
          startAt: Date.now(),
          durationMs: data.durationMs,
        },
      }));
    };

    const onSlotLooted = (): void => {
      playRandomLootCollectSound();
    };

    const onPiecesLooted = (): void => {
      playRandomCoinsGainSound();
    };

    const onXPLooted = (): void => {
      playSound(SFX_KEYS.XP_GAIN);
    };

    socket.on('lootcontainer:view', onView);
    socket.on('lootcontainer:error', onError);
    socket.on('lootcontainer:identify_started', onIdentifyStarted);
    socket.on('lootcontainer:slot_looted', onSlotLooted);
    socket.on('lootcontainer:pieces_looted', onPiecesLooted);
    socket.on('lootcontainer:xp_looted', onXPLooted);

    return () => {
      socket.off('lootcontainer:view', onView);
      socket.off('lootcontainer:error', onError);
      socket.off('lootcontainer:identify_started', onIdentifyStarted);
      socket.off('lootcontainer:slot_looted', onSlotLooted);
      socket.off('lootcontainer:pieces_looted', onPiecesLooted);
      socket.off('lootcontainer:xp_looted', onXPLooted);
    };
  }, [socket, containerId]);

  useEffect(() => {
    if (!isOpen) return;
    if (!containerId) return;
    requestOpen();
  }, [isOpen, containerId, requestOpen]);

  useEffect(() => {
    if (!socket) return;
    if (!containerId) return;
    if (!isOpen) return;

    const interval = window.setInterval(() => {
      setIdentifying((prev) => {
        const now = Date.now();
        let changed = false;
        const next: Record<number, IdentifyingState> = { ...prev };

        for (const state of Object.values(prev)) {
          const elapsed = now - state.startAt;
          if (elapsed >= state.durationMs) {
            changed = true;
            delete next[state.slotIndex];
            // Play identify complete sound
            playSound(SFX_KEYS.IDENTIFY_ITEM);
            socket.emit('lootcontainer:identifyComplete', {
              id: containerId,
              slotIndex: state.slotIndex,
              durationMs: state.durationMs,
            });
          }
        }

        return changed ? next : prev;
      });
    }, 100);

    return () => window.clearInterval(interval);
  }, [socket, containerId, isOpen]);

  // Click handler for slots - identify if unidentified, loot if identified
  const handleSlotClick = useCallback(
    (slotIndex: number): void => {
      if (!socket || !containerId) return;
      const slot = slotsByIndex[slotIndex];
      if (!slot) return;

      setError('');

      if (!slot.identified && !identifying[slotIndex]) {
        // Start identifying
        socket.emit('lootcontainer:identifyStart', { id: containerId, slotIndex });
      } else if (slot.identified) {
        // Loot the item
        socket.emit('lootcontainer:lootSlot', { id: containerId, slotIndex });
      }
    },
    [socket, containerId, slotsByIndex, identifying]
  );

  const handleMouseEnter = useCallback((e: React.MouseEvent, slot: LootSlotView): void => {
    if (!slot.identified || !slot.item) return;
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    setTooltip({
      visible: true,
      x: rect.right + 8,
      y: rect.top,
      slot,
    });
  }, []);

  const handleMouseLeave = useCallback((): void => {
    setTooltip({ visible: false, x: 0, y: 0, slot: null });
  }, []);

  const expiresLabel: string = view
    ? `${Math.max(0, Math.floor((view.expiresAt - Date.now()) / 1000))}s`
    : '-';

  if (!isOpen || !containerId) {
    return null;
  }
  
  return (
    <div className="loot-panel-v2 font-mono" style={{ position: 'fixed', zIndex: 1000, ...dragStyle }}>
      {/* Header */}
      <div className="loot-header-v2" onMouseDown={handleHeaderMouseDown} style={{ cursor: 'grab', userSelect: 'none' }}>
        <div className="loot-title-v2 font-lookout-24">WRECK LOOT</div>
        <button className="close-btn loot-close-v2" onClick={onClose}>×</button>
      </div>

        {/* Subheader with expiry */}
        <div className="loot-info-row">
          <div className="loot-expiry">Expires: {expiresLabel}</div>
        </div>

        {error && <div className="loot-error-v2">{error}</div>}

        {/* Item Grid - 5 columns x 4 rows like inventory */}
        <div className="loot-grid-v2">
          {Array.from({ length: 20 }).map((_, i) => {
            const slotIndex = i;
            const slot = slotsByIndex[slotIndex];
            const identifyState = identifying[slotIndex];
            const isIdentifying = !!identifyState;
            const isIdentified = slot?.identified === true;
            const hasItem = !!slot;
            const rarityColor = slot?.item?.rarity ? RARITY_COLORS[slot.item.rarity] : '#30363d';

            // Determine slot state for styling
            let slotClass = 'loot-slot-v2';
            if (!hasItem) {
              slotClass += ' empty';
            } else if (isIdentifying) {
              slotClass += ' identifying';
            } else if (!isIdentified) {
              slotClass += ' unidentified';
            } else {
              slotClass += ' identified';
            }

            return (
              <div
                key={slotIndex}
                className={slotClass}
                style={{ borderColor: isIdentified ? rarityColor : undefined }}
                onClick={() => handleSlotClick(slotIndex)}
                onMouseEnter={(e) => slot && handleMouseEnter(e, slot)}
                onMouseLeave={handleMouseLeave}
              >
                {!hasItem ? (
                  // Empty slot
                  <span className="empty-marker">-</span>
                ) : isIdentifying ? (
                  // Identifying in progress
                  <div className="identify-progress-container">
                    <div className="identify-icon">[?]</div>
                    <div className="identify-progress-bar">
                      <div
                        className="identify-progress-fill"
                        style={{
                          width: `${Math.min(100, ((Date.now() - identifyState.startAt) / identifyState.durationMs) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                ) : !isIdentified ? (
                  // Unidentified - show magnifying glass icon
                  <div className="unidentified-icon" title="Click to identify">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="6" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      <text x="11" y="14" textAnchor="middle" fontSize="8" fill="currentColor" stroke="none">?</text>
                    </svg>
                  </div>
                ) : (
                  // Identified item - show icon and quantity
                  <div className="identified-item">
                    <img
                      src={getIconSrc(slot.item?.icon, slot.itemId)}
                      alt={slot.item?.name || 'Item'}
                      style={{ width: 32, height: 32, objectFit: 'contain' }}
                      onError={(e) => {
                        const publicUrl: string = process.env.PUBLIC_URL || '';
                        const target = e.target as HTMLImageElement;
                        const fallbackSrc = isContrabandItemId(slot.itemId)
                          ? `${publicUrl}/assets/portraits/generic_contraband.png`
                          : `${publicUrl}/assets/unknown.png`;
                        if (target.src !== fallbackSrc) {
                          target.onerror = null;
                          target.src = fallbackSrc;
                        }
                      }}
                    />
                    {(slot.quantity || 0) > 1 && (
                      <span className="item-quantity-badge">{slot.quantity}</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Pieces section */}
        {view && view.pieces > 0 && (
          <div style={{ padding: '8px', borderTop: '1px solid #4a5568' }}>
            <button
              onClick={() => {
                if (socket && containerId) {
                  socket.emit('lootcontainer:takePieces', { id: containerId });
                }
              }}
              style={{
                width: '100%',
                padding: '4px 8px',
                backgroundColor: '#2d3748',
                color: '#ffd700',
                border: '1px solid #4a5568',
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: 'bold',
              }}
            >
              💰 {view.pieces} Pieces
            </button>
          </div>
        )}

        {/* Instructions */}
        <div className="loot-instructions">
          Click [?] to identify | Click item to loot
        </div>

        {/* Tooltip */}
        {tooltip.visible && tooltip.slot?.item && (
          <div
            className="loot-tooltip"
            style={{
              position: 'fixed',
              left: tooltip.x,
              top: tooltip.y,
              zIndex: 10001,
            }}
          >
            <div className="tooltip-name" style={{ color: RARITY_COLORS[tooltip.slot.item.rarity] || '#fff' }}>
              {tooltip.slot.item.name}
            </div>
            <div className="tooltip-type">{tooltip.slot.item.type}</div>
            {tooltip.slot.item.description && (
              <div className="tooltip-desc" style={{ fontFamily: 'var(--font-mono)', fontSize: '12px' }}>{tooltip.slot.item.description}</div>
            )}
            {tooltip.slot.item.stats && Object.keys(tooltip.slot.item.stats).length > 0 && (
              <div className="tooltip-stats">
                {Object.entries(tooltip.slot.item.stats).map(([key, val]) => (
                  <div key={key} className="tooltip-stat">+{val} {key}</div>
                ))}
              </div>
            )}
            <div className="tooltip-qty">Quantity: {tooltip.slot.quantity}</div>
          </div>
        )}
    </div>
  );
};
